<template>
  <header class="navbar glass-navbar">
    <div class="navbar__inner container">
      <!-- 左：LOGO + 文字（手機僅顯示小logo與主標可選擇隱藏次標） -->
      <router-link to="/" class="navbar__brand">
        <img :src="logoUrl" alt="logo" class="navbar__logo-img" />
        <div class="navbar__title">
          <div class="navbar__title-main">Web3富翁區塊學院</div>
          <div class="navbar__title-sub">🏅區塊項目速成課程（優越3.0）</div>
        </div>
      </router-link>

      <!-- 右：手機版漢堡鍵；桌面隱藏 -->
      <button
        class="navbar__toggle"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        aria-controls="site-menu"
        aria-label="切換導覽選單"
        @click="menuOpen = !menuOpen"
      >
        <span class="navbar__toggle-bar"></span>
        <span class="navbar__toggle-bar"></span>
        <span class="navbar__toggle-bar"></span>
      </button>

      <!-- 導覽選單：桌面水平排列；手機為下拉抽屜 -->
      <nav
        id="site-menu"
        class="navbar__menu"
        :class="{ 'is-open': menuOpen }"
        @click.self="menuOpen = false"
      >
        <router-link to="/courses" class="navbar__link" @click="closeMenu">進入課程主頁</router-link>
        <router-link to="/purchase" class="navbar__link" @click="closeMenu">購買課程</router-link>

        <template v-if="!authed">
          <router-link to="/login" class="navbar__link" @click="closeMenu">登入</router-link>
          <router-link to="/register" class="navbar__link navbar__link--accent" @click="closeMenu">註冊</router-link>
        </template>

        <template v-else>
          <div class="navbar__user">
            <span class="navbar__user-email" :title="user?.email">{{ user?.email || '已登入' }}</span>
            <button class="navbar__logout" @click="handleLogout">登出</button>
          </div>
        </template>
      </nav>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { isAuthed, currentUser, logout, ensureProfileLoaded } from '@/utils/auth'

const logoUrl = '/images/logo.png'
const router = useRouter()
const route = useRoute()

const authed = ref(false)
const user = ref(null)
const menuOpen = ref(false)

function refreshAuthState() {
  authed.value = isAuthed()
  user.value = currentUser()
}

function handleLogout() {
  logout()
  refreshAuthState()
  window.dispatchEvent(new Event('auth-changed'))
  router.push('/login')
  menuOpen.value = false
}

function closeMenu() {
  menuOpen.value = false
}

function onEsc(e) {
  if (e.key === 'Escape') menuOpen.value = false
}

onMounted(() => {
  refreshAuthState()
  ensureProfileLoaded().then(refreshAuthState).catch(() => {})

  const onStorage = (e) => {
    if (e.key === 'web3school_token' || e.key === 'web3school_user') refreshAuthState()
  }
  const onAuthChanged = () => refreshAuthState()

  window.addEventListener('storage', onStorage)
  window.addEventListener('auth-changed', onAuthChanged)
  window.addEventListener('keydown', onEsc)

  onBeforeUnmount(() => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('auth-changed', onAuthChanged)
    window.removeEventListener('keydown', onEsc)
  })
})

watch(() => route.fullPath, () => {
  refreshAuthState()
  menuOpen.value = false // 換頁自動收合
})
</script>

<style scoped>
/* ========== 共同樣式 ========== */
.navbar {
  position: absolute;
  top: 0; left: 0; width: 100%;
  z-index: 100;
  transition: all .3s ease;
}
.glass-navbar {
  background: rgba(255,255,255,.15);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  border-bottom: 1px solid rgba(255,255,255,.25);
  box-shadow: 0 4px 12px rgba(0,0,0,.15);
}

.navbar__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
}

.navbar__brand {
  display: flex; align-items: center; gap: 12px;
  text-decoration: none; color: #fff;
}

.navbar__logo-img {
  width: 48px; height: 48px; /* 手機更小一點 */
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,.3);
  box-shadow: 0 0 10px rgba(255,255,255,.25);
  object-fit: cover;
}

.navbar__title { display: inline-flex; flex-direction: column; min-width: 200px; line-height: 1.15; }
.navbar__title-main { font-weight: 800; font-size: 18px; color: #fff; letter-spacing: .5px; text-align: left; transform: scaleX(1.05); }
.navbar__title-sub { font-size: 12px; font-weight: 500; color: var(--brand-gold); letter-spacing: .5px; text-align: left; }

/* 右側選單（桌面） */
.navbar__menu {
  display: flex; align-items: center; gap: 16px;
}

/* 連結 */
.navbar__link {
  color: #fff; padding: 8px 16px; font-size: 15px; border-radius: 12px;
  background: rgba(255,255,255,.15);
  border: 1px solid rgba(255,255,255,.25);
  backdrop-filter: blur(10px);
  transition: all .2s ease;
}
.navbar__link:hover { background: rgba(255,255,255,.25); transform: translateY(-1px); }

/* 強調：註冊 */
.navbar__link--accent {
  background: linear-gradient(135deg, var(--brand-gold), var(--brand-accent));
  color: #111; font-weight: 600; border: none;
  box-shadow: 0 4px 15px rgba(247,206,70,.3);
}
.navbar__link--accent:hover { filter: brightness(1.05); }

/* 已登入狀態 */
.navbar__user {
  display: inline-flex; align-items: center; gap: 10px;
  background: rgba(255,255,255,.15);
  border: 1px solid rgba(255,255,255,.25);
  padding: 6px 10px; border-radius: 12px; backdrop-filter: blur(10px);
}
.navbar__user-email { color: #fff; font-size: 14px; font-weight: 600; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.navbar__logout {
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.25); color: #fff;
  padding: 6px 10px; border-radius: 10px; cursor: pointer; font-weight: 600; transition: all .2s ease;
}
.navbar__logout:hover { background: rgba(255,255,255,.22); transform: translateY(-1px); }

/* ========== 手機樣式（關鍵） ========== */
.navbar__toggle {
  display: none; /* 桌面隱藏 */
  width: 42px; height: 36px; border-radius: 10px;
  background: rgba(255,255,255,.15);
  border: 1px solid rgba(255,255,255,.25);
  backdrop-filter: blur(10px);
  cursor: pointer;
  align-items: center; justify-content: center;
  gap: 4px; padding: 6px 8px;
}
.navbar__toggle-bar {
  display: block; width: 22px; height: 2px; background: #fff; border-radius: 2px;
}

/* 手機下拉選單：預設隱藏，打開時變為抽屜 */
@media (max-width: 720px) {
  .navbar__inner { padding: 10px 0; }
  .navbar__title-sub { display: none; }         /* 手機隱藏次標題，保留主標與小logo */
  .navbar__title-main { font-size: 16px; }      /* 主標略縮小 */
  .navbar__logo-img { width: 44px; height: 44px; }

  /* 顯示漢堡鍵 */
  .navbar__toggle { display: inline-flex; }

  /* 導覽選單變成覆蓋式抽屜（從右滑出） */
  .navbar__menu {
    position: absolute;
    top: 100%;
    right: 12px;
    left: 12px;
    display: none;               /* 關閉時不佔位 */
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    margin-top: 8px;
    border-radius: 14px;
    background: rgba(20,20,20,.75);
    border: 1px solid rgba(255,255,255,.2);
    backdrop-filter: blur(12px);
    box-shadow: 0 12px 24px rgba(0,0,0,.25);
    animation: menu-drop .18s ease-out;
  }
  .navbar__menu.is-open { display: flex; }

  /* 手機下調整按鈕尺寸 */
  .navbar__link { width: 100%; text-align: center; padding: 10px 14px; }
  .navbar__user { width: 100%; justify-content: space-between; }
}

@keyframes menu-drop {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
